import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  clearPermissionsRegistryCache,
  fetchPermissionsRegistry,
  hydratePermissionsRegistryFromSession,
} from "../../services/permissionsService";
import {
  isActionAllowed,
  resolveTicketPermissions,
  resolveUserPermissions,
} from "../../utils/resolveUserPermissions";
import { useAuth } from "./authProvider";

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { user, userEmail } = useAuth();
  const [registry, setRegistry] = useState(() => (
    user ? hydratePermissionsRegistryFromSession() : null
  ));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      clearPermissionsRegistryCache();
      setRegistry(null);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    const sessionRegistry = hydratePermissionsRegistryFromSession();
    if (sessionRegistry) {
      setRegistry(sessionRegistry);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    const loadPermissions = async () => {
      setLoading(true);
      setError(null);

      try {
        const remoteRegistry = await fetchPermissionsRegistry();
        if (!cancelled) {
          setRegistry(remoteRegistry);
        }
      } catch (loadError) {
        if (!cancelled) {
          setRegistry({});
          setError(loadError instanceof Error ? loadError.message : "Permissions load failed");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const resolvedPermissions = useMemo(
    () => resolveUserPermissions(userEmail, registry),
    [registry, userEmail]
  );

  const featureFlags = resolvedPermissions.featureFlags;
  const actionFlags = resolvedPermissions.actionFlags;
  const ticketPermissions = useMemo(
    () => resolveTicketPermissions(actionFlags),
    [actionFlags]
  );

  const isActionVisible = useCallback(
    (group, action) => isActionAllowed(actionFlags, group, action),
    [actionFlags]
  );

  const value = useMemo(
    () => ({
      loading,
      error,
      registryLoaded: Boolean(user) && !loading,
      userEmail,
      featureFlags,
      actionFlags,
      tableActions: actionFlags,
      ticketPermissions,
      isActionVisible,
    }),
    [
      actionFlags,
      error,
      featureFlags,
      isActionVisible,
      loading,
      ticketPermissions,
      user,
      userEmail,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

PermissionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionsProvider");
  }
  return context;
};

export default PermissionsContext;
